/**
 * GalleryExportPage - Export options and integrations.
 *
 * Provides:
 * - URL export in various formats (Simple, Markdown, HTML, JSON, CSV)
 * - Sanity CMS integration
 * - Social media quick actions
 *
 * Composes existing GalleryImageUrlCopier and SanityGalleryManager components.
 *
 * @see /src/features/gallery/layouts/GalleryLayout.tsx - Parent layout
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Link2,
  Database,
  Share2,
  Copy,
  Download,
  FileJson,
  FileText,
  Code,
  CheckCircle,
  Image,
} from 'lucide-react'

import { useGalleryDetail } from '../context/GalleryDetailContext'
import { GalleryImageUrlCopier } from '../components/common/GalleryImageUrlCopier'
import { SanityGalleryManager } from '../components/detail/SanityGalleryManager'
import { useToast } from '@/hooks/use-toast'

/**
 * Export format type.
 */
type ExportFormat = 'simple' | 'markdown' | 'html' | 'json' | 'csv'

export default function GalleryExportPage() {
  const { toast } = useToast()
  const { gallery, images, stats } = useGalleryDetail()
  const [exportFormat, setExportFormat] = useState<ExportFormat>('simple')

  // Filter to approved images only for export
  const approvedImages = images.filter(
    (img) => img.review_status === 'approved' || img.image_type === 'gallery_photo'
  )

  // Generate export content based on format
  const generateExport = (format: ExportFormat): string => {
    const urls = approvedImages.map((img) => img.image_url).filter(Boolean) as string[]

    switch (format) {
      case 'simple':
        return urls.join('\n')

      case 'markdown':
        return urls.map((url, i) => `![Image ${i + 1}](${url})`).join('\n\n')

      case 'html':
        return urls
          .map((url, i) => `<img src="${url}" alt="Image ${i + 1}" loading="lazy" />`)
          .join('\n')

      case 'json':
        return JSON.stringify(
          approvedImages.map((img) => ({
            id: img.original_id,
            url: img.image_url,
            type: img.image_type,
            caption: img.caption,
            isCover: img.is_cover,
          })),
          null,
          2
        )

      case 'csv': {
        const headers = 'id,url,type,caption,is_cover'
        const rows = approvedImages.map(
          (img) =>
            `${img.original_id},"${img.image_url}","${img.image_type}","${img.caption || ''}",${img.is_cover || false}`
        )
        return [headers, ...rows].join('\n')
      }

      default:
        return urls.join('\n')
    }
  }

  // Copy to clipboard
  const handleCopy = async () => {
    const content = generateExport(exportFormat)
    try {
      await navigator.clipboard.writeText(content)
      toast({ title: 'Copied to clipboard' })
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' })
    }
  }

  // Download as file
  const handleDownload = () => {
    const content = generateExport(exportFormat)
    const extensions: Record<ExportFormat, string> = {
      simple: 'txt',
      markdown: 'md',
      html: 'html',
      json: 'json',
      csv: 'csv',
    }
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `gallery-${gallery?.id}-images.${extensions[exportFormat]}`
    a.click()
    URL.revokeObjectURL(url)
    toast({ title: 'Download started' })
  }

  const formatIcons: Record<ExportFormat, React.ReactNode> = {
    simple: <FileText className="h-4 w-4" />,
    markdown: <Code className="h-4 w-4" />,
    html: <Code className="h-4 w-4" />,
    json: <FileJson className="h-4 w-4" />,
    csv: <FileText className="h-4 w-4" />,
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="animate-fade-down">
        <h2 className="text-xl font-semibold">Export & Integrations</h2>
        <p className="text-sm text-muted-foreground">
          Export images and manage integrations for this gallery
        </p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50 animate-fade-up">
        <Image className="h-5 w-5 text-muted-foreground" />
        <div className="text-sm">
          <span className="font-medium">{approvedImages.length}</span>
          <span className="text-muted-foreground"> approved images available for export</span>
        </div>
        {stats.pending > 0 && (
          <Badge variant="warning" className="ml-auto">
            {stats.pending} pending review
          </Badge>
        )}
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="urls" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="urls" className="gap-2">
            <Link2 className="h-4 w-4" />
            Image URLs
          </TabsTrigger>
          <TabsTrigger value="sanity" className="gap-2">
            <Database className="h-4 w-4" />
            Sanity CMS
          </TabsTrigger>
          <TabsTrigger value="social" className="gap-2">
            <Share2 className="h-4 w-4" />
            Social Media
          </TabsTrigger>
        </TabsList>

        {/* URL Export Tab */}
        <TabsContent value="urls">
          <div className="space-y-4 stagger-children">
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Export Format</CardTitle>
                  <CardDescription>
                    Choose a format for exporting image URLs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Format Selection */}
                  <div className="flex flex-wrap gap-2">
                    {(['simple', 'markdown', 'html', 'json', 'csv'] as ExportFormat[]).map(
                      (format) => (
                        <Button
                          key={format}
                          variant={exportFormat === format ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setExportFormat(format)}
                          className="gap-2"
                        >
                          {formatIcons[format]}
                          {format.toUpperCase()}
                        </Button>
                      )
                    )}
                  </div>

                  {/* Preview */}
                  <div className="rounded-lg border bg-muted/30 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-muted-foreground font-medium">
                        Preview ({approvedImages.length} images)
                      </span>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={handleCopy}>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </Button>
                        <Button variant="ghost" size="sm" onClick={handleDownload}>
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                    <pre className="text-xs text-muted-foreground overflow-auto max-h-48 whitespace-pre-wrap font-mono">
                      {generateExport(exportFormat).slice(0, 1000)}
                      {generateExport(exportFormat).length > 1000 && '...'}
                    </pre>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button onClick={handleCopy} className="flex-1 gap-2">
                      <Copy className="h-4 w-4" />
                      Copy to Clipboard
                    </Button>
                    <Button variant="outline" onClick={handleDownload} className="flex-1 gap-2">
                      <Download className="h-4 w-4" />
                      Download File
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Alternative: Use existing copier component */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Quick Copy</CardTitle>
                  <CardDescription>
                    Copy individual URLs or filtered lists
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <GalleryImageUrlCopier
                    images={images as any}
                    galleryTitle={gallery?.title ?? 'Gallery'}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Sanity CMS Tab */}
        <TabsContent value="sanity">
          <div className="animate-fade-up">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sanity CMS Integration</CardTitle>
                <CardDescription>
                  Sync this gallery to your Sanity CMS project
                </CardDescription>
              </CardHeader>
              <CardContent>
                {gallery ? (
                  <SanityGalleryManager
                    gallery={{
                      ...gallery,
                      photos: [],
                      services: [],
                      tags: [],
                      photo_count: stats.total,
                      approved_photo_count: stats.approved,
                    } as any}
                    coverImageUrl={gallery.cover_image ?? undefined}
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Loading gallery data...
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Social Media Tab */}
        <TabsContent value="social">
          <div className="space-y-4 stagger-children">
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Create Social Post</CardTitle>
                  <CardDescription>
                    Use approved images from this gallery in a social media post
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
                    <CheckCircle className="h-8 w-8 text-success" />
                    <div>
                      <p className="font-medium">
                        {approvedImages.length} images ready for social
                      </p>
                      <p className="text-sm text-muted-foreground">
                        These approved images can be used in social media posts
                      </p>
                    </div>
                  </div>

                  <Button asChild className="w-full gap-2">
                    <Link to="/marketing/social/create">
                      <Share2 className="h-4 w-4" />
                      Create New Post
                    </Link>
                  </Button>

                  <Button variant="outline" asChild className="w-full gap-2">
                    <Link to="/marketing/calendar">
                      View Content Calendar
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Gallery Info for Posts</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gallery Title</span>
                    <span className="font-medium">{gallery?.title || '—'}</span>
                  </div>
                  {gallery?.job_number && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Job Number</span>
                      <span className="font-medium">#{gallery.job_number}</span>
                    </div>
                  )}
                  {gallery?.franchise_region && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Region</span>
                      <span className="font-medium capitalize">
                        {gallery.franchise_region.replace('_', ' ')}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
